import "src/styles/index.css"
import { Auth0Provider } from "@auth0/auth0-react"
import {
  ApiError,
  AppContextProvider,
  AuthenticationRequired,
  disabledQuery,
  ErrorFallback,
  getDomain,
  getEnv,
  Loader,
  ModalConfirmationProvider,
  NoTrailingSlash,
  queryClient,
  ToastsRoot,
  useImpersonification,
  useKeyboardDetection,
  useRelease,
  WithErrorBoundary
} from "@quortex/front-common"
import { ReactLocationAdapter } from "@quortex/front-common/adapters/react-location"
import { captureException, init } from "@sentry/browser"
import { Outlet, ReactLocation, Router } from "@tanstack/react-location"
import { QueryClientProvider, useSuspenseQuery } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { lazy, Suspense, useEffect } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { version } from "@/../package.json"
import { portalTree } from "./routes"

window.ENV = {}
window.ENV.REACT_APP_API_URL = import.meta.env.REACT_APP_API_URL

const FromBackOfficeBorder = lazy(() =>
  import("@quortex/front-common").then(module => ({ default: module.FromBackOfficeBorder }))
)

const Logout = lazy(() =>
  import("@quortex/front-common").then(module => ({ default: module.Logout }))
)

// Configuring sentry's dynamic options.
const sentryDsn = import.meta.env.REACT_APP_SENTRY_DSN

if (sentryDsn) {
  init({
    beforeSend(event) {
      const exception = event.exception?.values?.[0]

      if (exception) {
        const message = exception.value || ""

        if (
          message.includes("preload") ||
          message.startsWith("Failed to fetch dynamically imported module")
        ) {
          event.fingerprint = ["network-error", "dynamic-import"]
        }
      }

      return event
    },
    dsn: sentryDsn,
    environment: getEnv(),
    release: import.meta.env.REACT_APP_SENTRY_RELEASE
  })
}

const reactLocation = new ReactLocation()

const getAuth0ClientId = (isImpersonified: boolean) => {
  const backOfficeId = import.meta.env.REACT_APP_AUTH0_CLIENT_ID_QX_BACKOFFICE
  const appId = import.meta.env.REACT_APP_AUTH0_CLIENT_ID
  return isImpersonified ? backOfficeId : appId
}

const routes = [{ element: <Logout />, path: "logout" }, portalTree]

const App = () => {
  useKeyboardDetection()
  const { isImpersonified } = useImpersonification()

  // register app version on dom
  useEffect(() => {
    document.querySelector('meta[name="front-play-version"]')?.setAttribute("content", version)
  }, [])

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => <Loader error={error} isCover />}
      onError={error => {
        if (!(error instanceof ApiError)) {
          captureException(error)
        }
      }}
    >
      <Suspense fallback={<Loader isCover />}>
        <Auth0Provider
          authorizationParams={{
            audience: import.meta.env.REACT_APP_AUTH0_AUDIENCE,
            redirect_uri: window.location.origin
          }}
          cacheLocation={isImpersonified ? "memory" : "localstorage"}
          clientId={getAuth0ClientId(isImpersonified)}
          cookieDomain={getDomain() || undefined}
          domain={import.meta.env.REACT_APP_AUTH0_DOMAIN}
          issuer={import.meta.env.REACT_APP_AUTH0_ISSUER}
          useCookiesForTransactions
          useFormData
          useRefreshTokens
          useRefreshTokensFallback
          onRedirectCallback={appState => {
            const returnTo = appState?.returnTo
            if (returnTo) reactLocation.history.push(returnTo)
          }}
        >
          <QueryClientProvider client={queryClient}>
            <CheckRelease />
            <AppContextProvider
              apps={{
                ingest: import.meta.env.REACT_APP_INGEST_URL,
                link: import.meta.env.REACT_APP_LINK_URL,
                play: import.meta.env.REACT_APP_PLAY_URL,
                powervu: import.meta.env.REACT_APP_POWERVU_URL,
                subscription: import.meta.env.REACT_APP_SUBSCRIPTION_URL,
                switch: import.meta.env.REACT_APP_SWITCH_URL
              }}
              crispWebsiteId={import.meta.env.REACT_APP_CRISP_WEBSITE_ID}
              currentApp="play"
              devMode={import.meta.env.DEV}
              homeUrl={import.meta.env.REACT_APP_PORTAL_URL}
            >
              <AuthenticationRequired>
                <Router location={reactLocation} routes={routes}>
                  <ReactLocationAdapter>
                    <ToastsRoot />
                    {/* <PortalRouter /> */}
                    <NoTrailingSlash />
                    <ModalConfirmationProvider>
                      <WithErrorBoundary ErrorFallback={ErrorFallback}>
                        <Outlet />
                        {isImpersonified && <FromBackOfficeBorder />}
                      </WithErrorBoundary>
                    </ModalConfirmationProvider>
                  </ReactLocationAdapter>
                </Router>
              </AuthenticationRequired>
            </AppContextProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </Auth0Provider>
      </Suspense>
    </ErrorBoundary>
  )
}

const build = {
  date: import.meta.env.BUILD_DATE as string | undefined,
  id: import.meta.env.BUILD_ID as string | undefined
}

const CheckRelease = () => {
  const releaseQuery = useRelease()
  const { data: release } = useSuspenseQuery(build.date ? releaseQuery : disabledQuery)

  useEffect(() => {
    if (release && (release.build.id !== build.id || release.build.date !== build.date)) {
      console.info("Local build not matching remote build. Reloading…", {
        local: build,
        remote: release.build
      })
      window.location.reload()
    }
  })

  return null
}

export default App
